import type { ReactElement } from 'react';
import { Card } from '@/components/atoms/card';
import { Icon, type IconName } from '@/components/atoms/icon';
import { Micro } from '@/components/atoms/micro';
import { cn } from '@/lib/cn';

export type StatTone = 'neutral' | 'brand' | 'warm' | 'ok';

const TONE: Readonly<Record<StatTone, string>> = {
  neutral: 'bg-[var(--bg-soft)] text-[var(--ink-2)]',
  brand: 'bg-[var(--brand-wash)] text-[var(--brand-ink)]',
  warm: 'bg-[var(--warm-wash)] text-[var(--warm)]',
  ok: 'bg-[var(--ok-wash)] text-[var(--ok)]',
};

export type StatTileProps = {
  label: string;
  value: string;
  unit?: string;
  icon?: IconName;
  tone?: StatTone;
  delta?: string;
  className?: string;
};

export function StatTile({
  label, value, unit, icon, tone = 'neutral', delta, className,
}: StatTileProps): ReactElement {
  return (
    <Card padding="md" className={className} role="group" aria-label={label}>
      <div className="flex items-center justify-between mb-3">
        <Micro>{label}</Micro>
        {icon && (
          <div className={cn('w-7 h-7 rounded-[var(--r-sm)] flex items-center justify-center', TONE[tone])}>
            <Icon name={icon} size={13} />
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <div className="font-display text-[28px] font-semibold leading-none text-[var(--ink)] tabular-nums">
          {value}
        </div>
        {unit && <div className="text-[12px] text-[var(--ink-3)] font-medium">{unit}</div>}
      </div>
      {delta && (
        <div className="mt-2 text-[11px] font-semibold text-[var(--ok)] flex items-center gap-0.5">
          <Icon name="arr_u" size={10} />
          {delta} vs last week
        </div>
      )}
    </Card>
  );
}
